'use client';

import { useEffect, useState } from 'react';

export function HeroSection() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      label: 'Business Registration',
      headline: 'Launch Your Business',
      highlight: 'With Confidence',
      sub: 'End-to-end company registration, compliance and legal structuring — handled by experts so you focus on growth.',
    },
    {
      label: 'Startup Funding',
      headline: 'Fuel Your Vision',
      highlight: 'Secure the Capital',
      sub: 'Connect with investors, craft winning pitch decks and access funding channels tailored to your startup stage.',
    },
    {
      label: 'IT Solutions',
      headline: 'Accelerate with',
      highlight: 'Smart Technology',
      sub: 'Custom software, digital transformation and scalable IT infrastructure built for modern businesses.',
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const slide = slides[currentSlide];

  return (
    <section className="relative w-full overflow-hidden bg-slate-950" style={{ height: '78vh', minHeight: '560px', maxHeight: '860px' }}>
      {/* Background Video / Fallback Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1800&q=80"
          alt="Hero Background"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
      </div>

      

      {/* Accent bar top */}
      <div className="absolute top-0 left-0 right-0 h-[3px] z-10 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-600" />

      {/* Main Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-3xl">

            {/* Pill label */}
            <div
              className={`inline-flex items-center gap-2 mb-5 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '100ms' }}
            >
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-400 text-xs font-semibold tracking-[0.2em] uppercase">
                {slide.label}
              </span>
            </div>

            {/* Headline */}
            <h1
              key={currentSlide + '-h'}
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white mb-2 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms', fontFamily: "'Sora', sans-serif" }}
            >
              {slide.headline}
            </h1>
            <h1
              key={currentSlide + '-hl'}
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{
                transitionDelay: '300ms',
                fontFamily: "'Sora', sans-serif",
                background: 'linear-gradient(135deg, #f59e0b, #fb923c)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {slide.highlight}
            </h1>

            {/* Sub */}
            <p
              key={currentSlide + '-sub'}
              className={`text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl mb-8 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '400ms' }}
            >
              {slide.sub}
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-wrap gap-3 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '500ms' }}
            >
              <a
                href="/contact"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-sm font-semibold text-sm text-white tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)' }}
              >
                Get Free Consultation
                <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
              </a>
              <a
                href="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm font-semibold text-sm text-white tracking-wide border border-white/20 bg-white/5 backdrop-blur-sm hover:border-white/40 hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5"
              >
                Explore Services
              </a>
            </div>

            {/* Trust badges */}
            <div
              className={`flex flex-wrap items-center gap-6 mt-10 transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '650ms' }}
            >
              {[
                { num: '2000+', label: 'Businesses Registered' },
                { num: '98%', label: 'Client Satisfaction' },
                { num: '5+', label: 'Years of Expertise' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2">
                  <span className="text-amber-400 font-bold text-lg" style={{ fontFamily: "'Sora', sans-serif" }}>{b.num}</span>
                  <span className="text-slate-400 text-xs">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`transition-all duration-300 rounded-full ${i === currentSlide ? 'w-8 h-2 bg-amber-400' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
          />
        ))}
      </div>

      {/* Right image card */}
      <div
        className={`hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 z-10 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
        style={{ transitionDelay: '400ms' }}
      >
        <div className="relative w-72 h-80 rounded-md overflow-hidden border border-white/10 shadow-2xl">
          <img
            src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&q=80"
            alt="Business Professionals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3">
              <div className="text-white text-xs font-semibold mb-1">Trusted by 2000+ companies</div>
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <img
                    key={i}
                    src={`https://images.unsplash.com/photo-${i === 1 ? '1507003211169-0a1dd7228f2d' : i === 2 ? '1494790108377-be9c29b29330' : i === 3 ? '1500648767791-00dcc994a43e' : '1472099645785-5658abf4ff4e'}?w=40&h=40&fit=crop&q=60`}
                    alt="avatar"
                    className="w-7 h-7 rounded-full border-2 border-slate-800 object-cover"
                  />
                ))}
                <div className="w-7 h-7 rounded-full border-2 border-slate-800 bg-amber-500 flex items-center justify-center text-[9px] font-bold text-white">+</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}